import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Eye, Clock, Bell, Package, CheckCircle, XCircle, Fire, ArrowsClockwise,
} from "@phosphor-icons/react";
import { api, authHeaders } from "@/lib/api";
import PageHeader from "@/admin/components/PageHeader";
import OrderDetailDialog from "@/admin/components/OrderDetailDialog";

const STATUSES = ["all", "pending", "preparing", "ready", "delivered", "cancelled"];
const ACTIVE_STATUSES = new Set(["pending", "preparing", "ready"]);

const STATUS_COLOR = {
    pending: "bg-amber-100 text-amber-900",
    preparing: "bg-blue-100 text-blue-900",
    ready: "bg-emerald-100 text-emerald-900",
    delivered: "bg-zinc-200 text-zinc-700",
    cancelled: "bg-red-100 text-red-900",
};

const PERIODS = [
    { id: "today", label: "Hoy" },
    { id: "week",  label: "Semana" },
    { id: "month", label: "Mes" },
    { id: "all",   label: "Todos" },
];

// Compute the ET-based start of the requested period so filtering matches the
// deli's operating day (avoids UTC bugs at midnight).
function startOfPeriod(period) {
    const now = new Date();
    const etDate = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
    etDate.setHours(0, 0, 0, 0);
    if (period === "today") return etDate.getTime();
    if (period === "week") {
        // Monday as week start.
        const dow = (etDate.getDay() + 6) % 7;
        etDate.setDate(etDate.getDate() - dow);
        return etDate.getTime();
    }
    if (period === "month") {
        etDate.setDate(1);
        return etDate.getTime();
    }
    return 0; // all
}

function dayKey(iso) {
    // Group orders by ET date so a 11pm order and a 2am order the next day
    // don't accidentally end up under the same header.
    return new Date(iso).toLocaleDateString("en-CA", { timeZone: "America/New_York" });
}

function humanDay(key) {
    const d = new Date(key + "T12:00:00");
    return d.toLocaleDateString(undefined, {
        weekday: "long", month: "short", day: "numeric",
    });
}

function agoMin(iso) {
    const diff = Math.max(0, Date.now() - new Date(iso).getTime());
    return Math.floor(diff / 60000);
}

export default function Orders() {
    const [orders, setOrders] = useState([]);
    const [period, setPeriod] = useState("today");
    const [statusFilter, setStatusFilter] = useState("all");
    const [selected, setSelected] = useState(null);

    const fetchOrders = () =>
        api.get("/orders", { headers: authHeaders() })
            .then((r) => setOrders(r.data))
            .catch(() => {});

    useEffect(() => {
        fetchOrders();
        const id = setInterval(fetchOrders, 10000);
        return () => clearInterval(id);
    }, []);

    const update = async (id, status) => {
        try {
            await api.put(`/orders/${id}/status`, { status }, { headers: authHeaders() });
            toast.success(`Estado → ${status}`);
            fetchOrders();
        } catch {
            toast.error("Update failed");
        }
    };

    // Orders in the selected period (never applies status filter yet — we
    // need the unfiltered slice for the live-queue counts).
    const inPeriod = useMemo(() => {
        const from = startOfPeriod(period);
        return orders.filter((o) => new Date(o.created_at).getTime() >= from);
    }, [orders, period]);

    // Live queue = today's active orders (pending/preparing/ready), regardless
    // of the currently selected status filter. Always visible.
    const liveQueue = useMemo(() => {
        const from = startOfPeriod("today");
        return orders
            .filter((o) => ACTIVE_STATUSES.has(o.status) && new Date(o.created_at).getTime() >= from)
            .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    }, [orders]);

    const filtered = useMemo(() => {
        return inPeriod.filter((o) => statusFilter === "all" ? true : o.status === statusFilter);
    }, [inPeriod, statusFilter]);

    // Group history by day for the historical view.
    const grouped = useMemo(() => {
        const map = new Map();
        for (const o of filtered) {
            const k = dayKey(o.created_at);
            if (!map.has(k)) map.set(k, []);
            map.get(k).push(o);
        }
        return [...map.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));
    }, [filtered]);

    const counts = useMemo(() => ({
        pending: liveQueue.filter((o) => o.status === "pending").length,
        preparing: liveQueue.filter((o) => o.status === "preparing").length,
        ready: liveQueue.filter((o) => o.status === "ready").length,
    }), [liveQueue]);

    return (
        <div data-testid="admin-orders">
            <PageHeader
                kicker="Live queue"
                title="Pedidos"
                action={
                    <button
                        data-testid="orders-refresh"
                        onClick={fetchOrders}
                        className="inline-flex items-center gap-2 rounded-full bg-white border border-border px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-muted"
                    >
                        <ArrowsClockwise size={14} weight="bold" />
                        Refrescar
                    </button>
                }
            />

            {/* Live queue (today's active orders) */}
            {liveQueue.length > 0 && (
                <section data-testid="live-queue" className="mb-6">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                        <span className="inline-flex items-center gap-1.5 text-primary">
                            <Fire size={18} weight="fill" />
                            <p className="font-display font-black text-lg">Cola en directo</p>
                        </span>
                        <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                            · {liveQueue.length} activos
                        </span>
                        <div className="ml-auto flex gap-2 flex-wrap text-[10px] uppercase tracking-widest font-bold">
                            <QueueChip icon={Clock} label="Pending"    n={counts.pending}    tone="amber" />
                            <QueueChip icon={Package} label="Preparing" n={counts.preparing} tone="blue" />
                            <QueueChip icon={Bell} label="Ready"       n={counts.ready}       tone="emerald" />
                        </div>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {liveQueue.map((o) => (
                            <LiveCard
                                key={o.id}
                                order={o}
                                onOpen={() => setSelected(o)}
                                onUpdate={update}
                            />
                        ))}
                    </div>
                </section>
            )}

            {/* History filters + list */}
            <section data-testid="orders-history">
                <div className="flex flex-wrap gap-2 mb-3" data-testid="orders-period">
                    {PERIODS.map((p) => (
                        <button
                            key={p.id}
                            data-testid={`period-${p.id}`}
                            onClick={() => setPeriod(p.id)}
                            className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest border-2 transition ${
                                period === p.id
                                    ? "bg-foreground text-background border-foreground"
                                    : "bg-white text-foreground/70 border-border hover:border-foreground"
                            }`}
                        >
                            {p.label}
                        </button>
                    ))}
                    <span className="ml-auto self-center text-xs uppercase tracking-widest font-bold text-muted-foreground">
                        {filtered.length} pedidos
                    </span>
                </div>
                <div className="flex flex-wrap gap-2 mb-4" data-testid="orders-filters">
                    {STATUSES.map((s) => (
                        <button
                            key={s}
                            data-testid={`filter-${s}`}
                            onClick={() => setStatusFilter(s)}
                            className={`px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest border transition ${
                                statusFilter === s
                                    ? "bg-primary text-primary-foreground border-primary"
                                    : "bg-white text-foreground/60 border-border hover:border-foreground"
                            }`}
                        >
                            {s}
                        </button>
                    ))}
                </div>

                {filtered.length === 0 ? (
                    <div
                        data-testid="orders-empty"
                        className="bg-white rounded-3xl border border-dashed border-border p-12 text-center text-muted-foreground"
                    >
                        Sin pedidos para este período.
                    </div>
                ) : (
                    <div className="space-y-5">
                        {grouped.map(([day, list]) => {
                            const dayTotal = list.reduce((s, o) => s + (o.total || 0), 0);
                            return (
                                <div key={day} data-testid={`day-group-${day}`}>
                                    <header className="flex items-baseline justify-between mb-2 px-1">
                                        <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground">
                                            {humanDay(day)}
                                        </p>
                                        <p className="text-xs uppercase tracking-widest font-bold">
                                            {list.length} · ${dayTotal.toFixed(2)}
                                        </p>
                                    </header>
                                    <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
                                        {list.map((o) => (
                                            <OrderCard
                                                key={o.id}
                                                order={o}
                                                onOpen={() => setSelected(o)}
                                                onUpdate={update}
                                            />
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>

            <OrderDetailDialog
                order={selected}
                open={!!selected}
                onClose={() => setSelected(null)}
                onStatusUpdate={async (id, status) => {
                    await update(id, status);
                    setSelected((prev) => (prev ? { ...prev, status } : prev));
                }}
                onRefunded={(o) => { setSelected(o); fetchOrders(); }}
            />
        </div>
    );
}

function QueueChip({ icon: Icon, label, n, tone }) {
    const toneMap = {
        amber: "bg-amber-100 text-amber-900",
        blue: "bg-blue-100 text-blue-900",
        emerald: "bg-emerald-100 text-emerald-900",
    };
    return (
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 ${toneMap[tone]}`}>
            <Icon size={12} weight="bold" />
            {label} · {n}
        </span>
    );
}

// Bright card for the live queue — bigger buttons, elapsed time badge.
function LiveCard({ order: o, onOpen, onUpdate }) {
    const mins = agoMin(o.created_at);
    const urgent = mins >= 15;
    return (
        <article
            data-testid={`live-order-${o.id}`}
            className={`rounded-2xl border-2 p-4 bg-white transition hover:shadow-md ${
                urgent ? "border-red-400" : "border-border"
            }`}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                        #{o.id.slice(-6).toUpperCase()} · {o.fulfillment}
                    </p>
                    <h3 className="font-display font-black text-lg truncate">
                        {o.customer_name}
                    </h3>
                </div>
                <span className={`shrink-0 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${STATUS_COLOR[o.status]}`}>
                    {o.status}
                </span>
            </div>
            <p className={`mt-1 text-[11px] font-bold uppercase tracking-widest inline-flex items-center gap-1 ${urgent ? "text-red-600" : "text-muted-foreground"}`}>
                <Clock size={12} weight="bold" /> Hace {mins} min
            </p>
            <ul className="mt-3 space-y-0.5 text-sm max-h-20 overflow-y-auto">
                {o.items.slice(0, 4).map((it, i) => (
                    <li key={i} className="flex justify-between gap-2">
                        <span className="truncate"><span className="font-bold text-primary">{it.qty}×</span> {it.name}</span>
                    </li>
                ))}
                {o.items.length > 4 && (
                    <li className="text-[11px] text-muted-foreground">+{o.items.length - 4} más</li>
                )}
            </ul>
            <div className="mt-3 pt-3 border-t border-border flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Total</span>
                <span className="font-display font-black text-xl">${o.total.toFixed(2)}</span>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-1.5">
                <QuickBtn testId={`set-preparing-${o.id}`} active={o.status === "preparing"} tone="blue" onClick={() => onUpdate(o.id, "preparing")}>Prep.</QuickBtn>
                <QuickBtn testId={`set-ready-${o.id}`} active={o.status === "ready"} tone="emerald" onClick={() => onUpdate(o.id, "ready")}>Listo</QuickBtn>
                <QuickBtn testId={`set-delivered-${o.id}`} active={o.status === "delivered"} tone="zinc" onClick={() => onUpdate(o.id, "delivered")}>Ent.</QuickBtn>
            </div>
            <button
                data-testid={`view-order-${o.id}`}
                onClick={onOpen}
                className="mt-2 w-full inline-flex items-center justify-center gap-1.5 text-[10px] font-bold uppercase tracking-widest py-2 rounded-full bg-muted hover:bg-muted/70"
            >
                <Eye size={12} weight="bold" /> Detalles
            </button>
        </article>
    );
}

// Compact card for history (grouped by day).
function OrderCard({ order: o, onOpen, onUpdate }) {
    return (
        <article
            data-testid={`order-${o.id}`}
            className="bg-white rounded-2xl border border-border p-4 hover:shadow-md transition"
        >
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                        #{o.id.slice(-6).toUpperCase()} · {o.fulfillment}
                    </p>
                    <h3 className="font-display font-black truncate">{o.customer_name}</h3>
                    <p className="text-[11px] text-muted-foreground">
                        {new Date(o.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </p>
                </div>
                <span className={`shrink-0 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${STATUS_COLOR[o.status] || "bg-muted"}`}>
                    {o.status}
                </span>
            </div>
            <ul className="mt-2 text-xs text-muted-foreground max-h-16 overflow-y-auto">
                {o.items.slice(0, 3).map((it, i) => (
                    <li key={i} className="truncate">{it.qty}× {it.name}</li>
                ))}
                {o.items.length > 3 && <li>+{o.items.length - 3} más</li>}
            </ul>
            <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
                <span className="font-display font-black text-lg">${o.total.toFixed(2)}</span>
                <div className="flex gap-1">
                    {o.status !== "delivered" && o.status !== "cancelled" && (
                        <button
                            data-testid={`quick-deliver-${o.id}`}
                            onClick={() => onUpdate(o.id, "delivered")}
                            className="h-8 w-8 grid place-items-center rounded-full bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                            title="Marcar entregado"
                        >
                            <CheckCircle size={14} weight="bold" />
                        </button>
                    )}
                    {o.status !== "cancelled" && o.status !== "delivered" && (
                        <button
                            data-testid={`quick-cancel-${o.id}`}
                            onClick={() => onUpdate(o.id, "cancelled")}
                            className="h-8 w-8 grid place-items-center rounded-full bg-red-50 text-red-600 hover:bg-red-100"
                            title="Cancelar"
                        >
                            <XCircle size={14} weight="bold" />
                        </button>
                    )}
                    <button
                        data-testid={`view-order-${o.id}`}
                        onClick={onOpen}
                        className="h-8 w-8 grid place-items-center rounded-full bg-muted hover:bg-muted/70"
                        title="Detalles"
                    >
                        <Eye size={14} weight="bold" />
                    </button>
                </div>
            </div>
        </article>
    );
}

function QuickBtn({ testId, active, tone, onClick, children }) {
    const toneMap = {
        blue: "bg-blue-500 hover:bg-blue-600 text-white",
        emerald: "bg-emerald-600 hover:bg-emerald-700 text-white",
        zinc: "bg-zinc-700 hover:bg-zinc-800 text-white",
    };
    return (
        <button
            data-testid={testId}
            onClick={onClick}
            className={`text-[10px] font-bold uppercase tracking-widest py-2 rounded-full transition ${
                active ? "ring-2 ring-offset-1 ring-primary " : ""
            }${toneMap[tone]}`}
        >
            {children}
        </button>
    );
}
